import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useDropdownPlacement from 'application/hooks/useDropdownPlacement';
import {
  ADDRESSES_DROPDOWN_GAP,
  ADDRESSES_DROPDOWN_MAX_HEIGHT,
  ADDRESSES_DROPDOWN_MIN_HEIGHT,
  ADDRESSES_DROPDOWN_PREFERRED_HEIGHT,
  ADDRESSES_DROPDOWN_VIEWPORT_GAP,
  DATA_TEST_IDS,
} from '../consts';
import type { AddressReportPointsSelectProps } from './types';

const getNormalizedValue = (value: string) => value.trim().toLowerCase();
export const useAddressReportPointsSelectViewModel = ({
  addresses,
  isLoading,
  selectedAddressIds,
  onChange,
}: AddressReportPointsSelectProps) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isTagFilterOpen, setIsTagFilterOpen] = useState(false);
  const [isExactSearch, setIsExactSearch] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);

  useEffect(() => {
    const handleDocumentPointerDown = (event: PointerEvent) => {
      const target = event.target;

      if (!(target instanceof Element)) {
        return;
      }

      if (!containerRef.current?.contains(target)) {
        setIsDropdownOpen(false);
        setIsTagFilterOpen(false);
      }
    };

    document.addEventListener('pointerdown', handleDocumentPointerDown);

    return () => document.removeEventListener('pointerdown', handleDocumentPointerDown);
  }, []);

  const dropdownState = useDropdownPlacement({
    containerRef,
    isOpen: isDropdownOpen,
    gap: ADDRESSES_DROPDOWN_GAP,
    maxHeight: ADDRESSES_DROPDOWN_MAX_HEIGHT,
    minHeight: ADDRESSES_DROPDOWN_MIN_HEIGHT,
    preferredHeight: ADDRESSES_DROPDOWN_PREFERRED_HEIGHT,
    viewportGap: ADDRESSES_DROPDOWN_VIEWPORT_GAP,
  });

  const selectedAddressIdSet = useMemo(() => new Set(selectedAddressIds), [selectedAddressIds]);

  const filteredAddresses = useMemo(() => {
    const normalizedSearch = getNormalizedValue(search);

    return addresses.filter((address) => {
      const searchableAddressText = `${address.address} ${address.comment || ''}`;
      const isMatchedBySearch =
        !normalizedSearch || getNormalizedValue(searchableAddressText).includes(normalizedSearch);

      if (!isMatchedBySearch || selectedTagIds.length === 0) {
        return isMatchedBySearch;
      }

      const addressTagIds = new Set(address.tags.map((tag) => tag.id));

      if (isExactSearch) {
        return selectedTagIds.every((tagId) => addressTagIds.has(tagId));
      }

      return selectedTagIds.some((tagId) => addressTagIds.has(tagId));
    });
  }, [addresses, isExactSearch, search, selectedTagIds]);

  const filteredAddressIds = useMemo(() => filteredAddresses.map((address) => address.id), [filteredAddresses]);

  const selectedCount = selectedAddressIds.length;
  const totalCount = addresses.length;
  const hasSelectedAddresses = selectedCount > 0;
  const hasUnselectedAddresses = filteredAddressIds.some((addressId) => !selectedAddressIdSet.has(addressId));
  const dropdownId = DATA_TEST_IDS.AUDIO_REPORT_ADDRESSES_DROPDOWN;

  const onDropdownToggle = useCallback(() => {
    setIsDropdownOpen((currentValue) => {
      const nextValue = !currentValue;

      if (!nextValue) {
        setIsTagFilterOpen(false);
      }

      return nextValue;
    });
  }, []);

  const onSearchChange = useCallback((value: string) => {
    setSearch(value);
  }, []);

  const onExactSearchChange = useCallback((value: boolean) => {
    setIsExactSearch(value);
  }, []);

  const onAddressChange = useCallback(
    (addressId: string, value: boolean) => {
      setIsTagFilterOpen(false);

      if (value) {
        onChange(selectedAddressIds.includes(addressId) ? selectedAddressIds : [...selectedAddressIds, addressId]);

        return;
      }

      onChange(selectedAddressIds.filter((currentAddressId) => currentAddressId !== addressId));
    },
    [onChange, selectedAddressIds]
  );

  const onSelectAll = useCallback(() => {
    onChange(Array.from(new Set([...selectedAddressIds, ...filteredAddressIds])));
  }, [filteredAddressIds, onChange, selectedAddressIds]);

  const onReset = useCallback(() => {
    onChange([]);
  }, [onChange]);

  return {
    containerRef,
    dropdownId,
    dropdownState,
    filteredAddresses,
    hasSelectedAddresses,
    hasUnselectedAddresses,
    isDropdownOpen,
    isExactSearch,
    isLoading,
    isTagFilterOpen,
    search,
    selectedAddressIdSet,
    selectedCount,
    selectedTagIds,
    totalCount,
    onAddressChange,
    onDropdownToggle,
    onExactSearchChange,
    onReset,
    onSearchChange,
    onSelectAll,
    onOpenChange: setIsTagFilterOpen,
    onSelectedTagIdsChange: setSelectedTagIds,
  };
};
